import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import {
  CreateNewFolderOutlined,
  DeleteForeverOutlined,
  EditOutlined,
  PlaylistAddOutlined,
  PlaylistRemoveOutlined,
  RefreshOutlined,
  SvgIconComponent,
} from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import color from 'src/assets/_util.scss';
import { ContextMenu, ContextMenuProps } from 'src/components/core/ContextMenu';
import { HStack } from 'src/components/core/Stack';
import { ViewProps } from 'src/components/core/View';
import { useCatalogPageContext } from 'src/components/Insights/Catalog/CatalogPageProvider';
import AddConversationsDialog from 'src/components/Insights/Dialogs/AddConversationsDialog';
import ConfirmationDialog from 'src/components/Insights/Dialogs/ConfirmationDialog';
import CreateCatalogDialog from 'src/components/Insights/Dialogs/CreateCatalogDialog';
import DeleteConversationsDialog from 'src/components/Insights/Dialogs/DeleteConversationsDialog';
import UpdateCatalogDialog from 'src/components/Insights/Dialogs/UpdateCatalogDialog';
import useDialogState from 'src/components/Insights/utils/dialog';
import {
  addConversations,
  deleteCatalog,
} from 'src/redux/catalog/catalog-slice';
import { Conversation } from 'src/types/conversation';
import { Catalog } from 'src/types/insights';

export function CatalogContextMenu(contextMenuProps: ContextMenuProps) {
  const history = useHistory();
  const { catalog, conversations, dispatch, showModal, t } =
    useCatalogPageContext();
  const [dialogState, openDialog, closeDialog] = useDialogState();
  const isMobile = catalog.mobile;

  const refreshCatalog = () => {
    const request: [Catalog['id'], Conversation['id'][]] = [
      catalog.id,
      conversations.map((conversation) => conversation.id),
    ];
    dispatch(addConversations(request));
  };

  const showDialog = (dialogName: string) => () => {
    contextMenuProps.setOpen(false);
    openDialog(dialogName)();
  };

  return (
    <ContextMenu
      {...contextMenuProps}
      style={{
        background: color.gray200,
        border: `1px solid ${color.gray300}`,
        ...contextMenuProps.style,
      }}
    >
      {!isMobile && (
        <MenuItem
          text={t('insights.refresh_highlights')}
          Icon={RefreshOutlined}
          onClick={showDialog('refreshCatalog')}
        />
      )}
      {!isMobile && (
        <MenuItem
          text={t('insights.import_highlights')}
          Icon={PlaylistAddOutlined}
          onClick={showDialog('addConversations')}
        />
      )}
      {!isMobile && (
        <MenuItem
          text={t('insights.remove_highlights')}
          Icon={PlaylistRemoveOutlined}
          onClick={showDialog('deleteConversations')}
        />
      )}
      <MenuItem
        text={t('insights.update_catalog')}
        Icon={EditOutlined}
        onClick={() => {
          contextMenuProps.setOpen(false);
          showModal(<UpdateCatalogDialog />);
        }}
      />
      {!isMobile && (
        <MenuItem
          text={t('insights.delete_catalog')}
          Icon={DeleteForeverOutlined}
          onClick={showDialog('deleteCatalog')}
        />
      )}
      <MenuItem
        text={t('insights.create_catalog')}
        Icon={CreateNewFolderOutlined}
        onClick={showDialog('newCatalog')}
        noDivider
      />

      {catalog && (
        <>
          <Dialog
            disableRestoreFocus
            onClose={closeDialog}
            open={Boolean(dialogState['refreshCatalog'])}
          >
            <DialogTitle>{t('insights.refresh_highlights_title')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t('insights.refresh_highlights_description')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog('refreshCatalog')}>
                {t('common.cancel')}
              </Button>
              <Button onClick={refreshCatalog}>{t('common.refresh')}</Button>
            </DialogActions>
          </Dialog>

          <AddConversationsDialog
            catalog={catalog}
            closeDialog={closeDialog('addConversations')}
            conversations={conversations}
            open={Boolean(dialogState['addConversations'])}
          />

          <ConfirmationDialog
            closeDialog={closeDialog('deleteCatalog')}
            confirmationText="delete"
            description="Are you sure you want to delete this catalog?"
            onSuccess={() => {
              dispatch(
                deleteCatalog({
                  data: catalog.id,
                  callback: () => history.push('/insights'),
                })
              );
            }}
            open={Boolean(dialogState['deleteCatalog'])}
            submitText="Delete"
            title="Delete Catalog"
          />

          <DeleteConversationsDialog
            catalog={catalog}
            closeDialog={closeDialog('deleteConversations')}
            conversations={conversations}
            open={Boolean(dialogState['deleteConversations'])}
          />

          <CreateCatalogDialog
            closeDialog={closeDialog('newCatalog')}
            open={Boolean(dialogState['newCatalog'])}
          />
        </>
      )}
    </ContextMenu>
  );
}

function MenuItem({
  text,
  Icon = null,
  noDivider = false,
  ...viewProps
}: {
  text?: string;
  Icon?: SvgIconComponent | null;
  noDivider?: boolean;
} & ViewProps) {
  return (
    <HStack
      sx={{
        p: '16px',
        gap: '24px',
        justifyContent: 'space-between',
        color: color.foraPurpleDark,
        borderBottom: noDivider ? '' : `1px solid ${color.gray300}`,
        fontWeight: 600,
        fontSize: '16px',
        cursor: 'pointer',
        ':hover': {
          backgroundColor: color.gray300,
        },
        ...viewProps.sx,
      }}
      {...viewProps}
    >
      {text}
      {Icon && <Icon width={12} />}
    </HStack>
  );
}
